import { useEffect } from 'react';
import { pullJobStatus } from '../sync/progressPullSync';
import { flushPendingProgress } from '../sync/progressSync';

const POLL_INTERVAL_MS = 15_000;

/**
 * Keeps a job's progress up to date while its screen is open — both
 * directions:
 *  - pulls what other photographers on the same job have marked (see
 *    progressPullSync.ts)
 *  - retries pushing this device's own not-yet-synced marks
 *    (progressSync.ts's flushPendingProgress) — that push is otherwise
 *    only ever attempted once, immediately when a photographer taps
 *    "Mark photographed". On the weak/patchy WiFi this app is built for,
 *    that one attempt can easily fail (or the mark can happen while
 *    fully offline), and without a retry that event would sit 'pending'
 *    forever — silently defeating the whole point of a second
 *    photographer ever seeing it. Found while testing this feature
 *    end-to-end, not by request; see docs/PRODUCTION_READINESS.md.
 *
 * Cheap and safe to mount on every screen that shows subject status (job
 * overview, group list, the QR card itself) — both calls no-op instantly
 * while offline, and pullJobStatus no-ops for a job that only exists
 * locally (never published to the server).
 */
export function useJobStatusSync(jobId: string | undefined): void {
  useEffect(() => {
    if (!jobId) return;

    function tick() {
      void flushPendingProgress();
      void pullJobStatus(jobId!);
    }

    tick();
    const interval = setInterval(tick, POLL_INTERVAL_MS);

    // Catch up right away on reconnect / returning to the tab, rather
    // than waiting out the rest of the current interval.
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    window.addEventListener('online', tick);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      window.removeEventListener('online', tick);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [jobId]);
}
